/**
 * The building detail panel.
 *
 * The largest piece of the legacy map: ~400 lines that built one HTML string from
 * a 37-field response and assigned it to innerHTML. Everything here that looks
 * like defensiveness is a bug that page had — a null interpolated as "null", a
 * per-dwelling cost labelled as a building total, a top energy rating shown for a
 * warehouse that was never assessed.
 *
 * Reading and shaping happen in lib/detail.ts, which is testable without a DOM.
 * This file is layout: it decides where things go, not what they mean.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { BuildingDetail as Detail, RoadmapStep } from "./lib/detail";
import {
  facadePhotoUrl, hasEnergyData, readBuildingDetail, studyLink,
} from "./lib/detail";
import { fmt, fmtCount, fmtEuro, fmtPct } from "./lib/format";
import { letterColor } from "./lib/letters";

import "./BuildingDetail.css";

/** One figure with its unit and label — the panel's smallest unit of content. */
function Card({ value, unit, label }: {
  value: string; unit: string; label: string;
}) {
  return (
    <div className="bd-card">
      <div className="bd-card-value">{value}</div>
      <div className="bd-card-unit">{unit}</div>
      <div className="bd-card-label">{label}</div>
    </div>
  );
}

function Step({ step }: { step: RoadmapStep }) {
  return (
    <li className={step.passive ? "bd-step passive" : "bd-step active"}>
      <div className="bd-step-head">Paso {step.step} · {step.title}</div>
      {step.descripcion && <div className="bd-step-desc">{step.descripcion}</div>}
      <div className="bd-step-facts">
        {step.facts.map((f) => <span key={f}>{f}</span>)}
        {/* Pushed to the end of the row: the cost is what a reader scans for, so
            it should line up down the column whatever precedes it. */}
        <strong className="bd-step-cost">{step.cost}</strong>
      </div>
      {step.saving && <div className="bd-step-saving">{step.saving}</div>}
      {step.note && <div className="bd-step-note">{step.note}</div>}
    </li>
  );
}

/** The facade photo, or nothing at all if Catastro has none for this building. */
function Facade({ localId }: { localId: string }) {
  const [failed, setFailed] = useState(false);
  // Reset when the building changes, or one missing photo would hide every
  // subsequent building's.
  useEffect(() => { setFailed(false); }, [localId]);

  if (failed) return null;
  return (
    <figure className="bd-facade">
      <img
        src={facadePhotoUrl(localId)}
        alt={`Fachada del edificio ${localId}`}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
      <figcaption>Sede Electrónica del Catastro</figcaption>
    </figure>
  );
}

function EnergySection({ d }: { d: Detail }) {
  const letter = d.letra ?? "?";
  const target = d.recomendacion.letraObjetivo;

  return (
    <section className="bd-section">
      <h3>Calificación energética (RD 390/2021)</h3>
      <div className="bd-rating">
        {/* letterColor already falls back to the no-data grey for anything that
            is not A–G, so an unexpected value paints grey rather than nothing. */}
        <div className="bd-letter" style={{ background: letterColor(d.letra) }}>
          {letter}
        </div>
        <div>
          <div className="bd-rating-line">
            EPnr <strong>{fmt(d.epnrenM2, 1, "kWh/m²·año")}</strong>
            {" · "}
            CO₂ <strong>{fmt(d.co2M2, 1, "kgCO₂/m²·año")}</strong>
          </div>
          {/* The zone and typology the legacy panel repeated here are stated in
              the facts grid directly above, which in this layout sits a few
              millimetres away. Two copies of one fact is how they end up
              disagreeing.

              Said explicitly in both directions below. "No obligation" is a fact
              about the building — most are under none — and blank space there
              reads as missing data instead. */}
          <div className="bd-rating-sub">
            {target
              ? <>Objetivo EED: letra <strong>{target}</strong>
                  {d.eedDeadline
                    ? <> · plazo {d.eedDeadline}</>
                    : <> · sin plazo asignado</>}</>
              : <>Sin obligación normativa EED</>}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BuildingDetail({ localId, onClose }: {
  localId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    // Cleared rather than kept: leaving the previous building on screen while the
    // next loads means a reader who clicks twice quickly reads the first one's
    // figures under the second one's reference.
    setDetail(null);

    api.buildingDetail(localId)
      .then((body) => { if (active) setDetail(readBuildingDetail(body)); })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof ApiError && err.status === 404
          ? "No tenemos datos de este edificio."
          : "No se ha podido cargar el detalle del edificio.");
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [localId]);

  const rec = detail?.recomendacion;

  return (
    <div className="bd-panel">
      <header className="bd-head">
        <div>
          <div className="bd-head-label">Edificio</div>
          <code className="bd-ref">{localId}</code>
        </div>
        <button type="button" className="bd-close" onClick={onClose}
                aria-label="Cerrar detalle del edificio">×</button>
      </header>

      {loading && <p className="bd-note">Cargando detalle…</p>}
      {error && <p className="bd-error" role="alert">{error}</p>}

      {detail && (
        <>
          <Facade localId={localId} />

          <section className="bd-section">
            <h3>Datos del edificio</h3>
            <div className="bd-cards">
              <Card value={fmt(detail.superficieM2)} unit="m²"
                    label="Superficie" />
              <Card value={fmtCount(detail.alturas)} unit="plantas"
                    label="Alturas" />
              <Card value={fmtCount(detail.nViviendas)} unit="viviendas"
                    label="Unidades" />
            </div>
            <dl className="bd-facts">
              <dt>Uso</dt><dd>{detail.uso ?? "–"}</dd>
              <dt>Época</dt><dd>{detail.epoca ?? "–"}</dd>
              <dt>Zona climática</dt><dd>{detail.zonaClimatica ?? "–"}</dd>
              <dt>Tipología</dt><dd>{detail.cluster ?? "–"}</dd>
            </dl>
          </section>

          {!hasEnergyData(detail) ? (
            /* The letter-A artifact made visible rather than presented as a
               rating: a building with no measurements comes back rated "A", and
               37 335 of them do. Saying why there is nothing is more useful than
               a column of en dashes. */
            <p className="bd-note">
              Sin datos energéticos. Ocurre en edificios industriales, agrícolas y
              sin uso residencial declarado, que el modelo no evalúa — la letra que
              figura para ellos no es una calificación.
            </p>
          ) : (
            <>
              <EnergySection d={detail} />

              <section className="bd-section">
                <h3>Demanda</h3>
                <div className="bd-cards">
                  <Card value={fmt(detail.heatM2, 1)} unit="kWh/m²·año"
                        label="Calefacción" />
                  <Card value={fmt(detail.coolM2, 1)} unit="kWh/m²·año"
                        label="Refrigeración" />
                </div>
              </section>

              <section className="bd-section">
                <h3>Instalaciones actuales</h3>
                <p className="bd-systems">{detail.sistemaLabel ?? "–"}</p>
                {/* Which of the two the reader is looking at. An inferred system
                    is a typology guess, and presenting it as declared fact is how
                    a survey answer ends up contradicted by our own map. */}
                <p className="bd-note-inline">
                  {detail.sistemaSource === "auto"
                    ? `Estimadas a partir de la tipología ${detail.sistemaTypology ?? ""} `
                      + "(SPAHOUSE-C), no declaradas."
                    : "Declaradas por el propietario."}
                </p>
              </section>

              {(detail.costeAero !== null || detail.costeGeo !== null) && (
                <section className="bd-section">
                  <h3>Coste anual de energía</h3>
                  <div className="bd-cards">
                    <Card value={fmtEuro(detail.costeAero)} unit="€/año"
                          label="Aerotermia" />
                    <Card value={fmtEuro(detail.costeGeo)} unit="€/año"
                          label="Geotermia" />
                    <Card value={fmtEuro(detail.ahorroCoste)} unit="€/año"
                          label="Ahorro" />
                  </div>
                </section>
              )}

              {rec && rec.roadmap.length > 0 && (
                <section className="bd-section">
                  <h3>Plan de actuación</h3>
                  <ol className="bd-steps">
                    {rec.roadmap.map((s) => <Step key={s.step} step={s} />)}
                  </ol>
                  {rec.ahorroEmisionesPct !== null && (
                    <p className="bd-note-inline">
                      Reducción de emisiones estimada al completar el plan:{" "}
                      {fmtPct(rec.ahorroEmisionesPct)}.
                    </p>
                  )}
                </section>
              )}

              {rec && rec.ayudas.length > 0 && (
                <section className="bd-section">
                  <h3>Ayudas aplicables</h3>
                  <ul className="bd-subsidies">
                    {rec.ayudas.map((a) => (
                      <li key={a.nombre}>
                        <div className="bd-sub-name">{a.nombre}</div>
                        {a.cobertura && (
                          <div className="bd-sub-cov">{a.cobertura}</div>
                        )}
                        {a.requisito && (
                          <div className="bd-sub-req">{a.requisito}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {/* The study, offered from every building whether or not it has energy
              data: how a home is lived in during a heatwave is not conditional on
              our having modelled it. New tab, because the map holds megabytes of
              loaded GeoJSON that a navigation would throw away. */}
          <a className="bd-study" href={studyLink(localId)}
             target="_blank" rel="noopener noreferrer">
            ¿Sufres calor en este edificio? Cuéntanoslo
            <span> 2 minutos · no genera informe</span>
          </a>
        </>
      )}
    </div>
  );
}
